/**
 * smartBack — intelligent navigation primitive.
 *
 * Premium apps (Calm, Headspace, Apple Music, Spotify) never strand
 * a user on `/` after they land via a deep link or share. Instead,
 * each route knows its contextual parent — the screen a member would
 * logically have come from inside the app.
 *
 * Behaviour:
 *   1. If there's real browser history (length > 1) → navigate(-1).
 *      This preserves the natural pop-stack feel when a member is
 *      browsing inside the app.
 *   2. Otherwise (cold load / shared link / direct URL) → resolve a
 *      contextual parent for the current pathname.
 *   3. Final fallback → "/".
 *
 * Two lookup maps make the semantics explicit:
 *
 *   EXACT_PARENTS — fires ONLY when pathname === key.
 *     e.g.  /abundance/my-visions → /daily-essentials
 *           (the listing itself ← its parent section)
 *
 *   CHILD_PARENTS — fires when pathname.startsWith(key + "/").
 *     e.g.  /abundance/immerse → /daily-essentials
 *           covers /abundance/immerse/wealth etc.
 *     e.g.  /my-journeys → /my-journeys
 *           covers /my-journeys/abc123 → goes back to the listing.
 *
 * Longest-prefix wins inside CHILD_PARENTS.
 */

// Exact-path mappings — pathname must match the key exactly.
const EXACT_PARENTS = {
  // Daily Essentials surfaces.
  "/abundance/my-visions":      "/daily-essentials",

  // Abundance sub-tools — most return to the Abundance home.
  "/abundance/peak-state":      "/abundance",
  "/abundance/winning-moment":  "/abundance",
  "/abundance/edit":            "/abundance",
  "/abundance/manifestations":  "/abundance",
};

// Child-prefix mappings — pathname.startsWith(key + "/").
const CHILD_PARENTS = {
  // State immersions launched from Daily Immersion. Cold-load
  // shares should return to Daily Essentials, the source surface.
  "/abundance/immerse":      "/daily-essentials",
  // Children of My Visions (e.g. an edit screen) → back to the
  // gallery, then the user can press back again to keep going up.
  "/abundance/my-visions":   "/abundance/my-visions",

  // Section listings — any deep child returns to the section root.
  "/my-journeys":            "/my-journeys",
  "/private-suite":          "/private-suite",
  "/practices":              "/practices",
  "/networker":              "/networker",
  "/atelier":                "/atelier",
  "/abundance":              "/abundance",
  "/team-alignment":         "/team-alignment",
  "/admin":                  "/admin",
};

/**
 * Resolve the contextual parent for an arbitrary pathname.
 */
export function parentFor(pathname) {
  if (!pathname || pathname === "/") return "/";
  // Strip trailing slash + query/hash for a clean match.
  const clean = pathname.split("?")[0].split("#")[0].replace(/\/+$/, "");

  // 1) Exact match wins outright.
  if (EXACT_PARENTS[clean]) return EXACT_PARENTS[clean];

  // 2) Longest-prefix child match.
  let bestKey = null;
  for (const key of Object.keys(CHILD_PARENTS)) {
    if (clean.startsWith(key + "/")) {
      if (!bestKey || key.length > bestKey.length) bestKey = key;
    }
  }
  if (bestKey) return CHILD_PARENTS[bestKey];

  // 3) Final fallback.
  return "/";
}

/**
 * Smart back navigation — call from any back/exit button.
 *   navigate  React Router's navigate fn
 *   pathname  The current location.pathname
 */
export function smartBack(navigate, pathname) {
  if (typeof window !== "undefined" && window.history.length > 1) {
    navigate(-1);
    return;
  }
  navigate(parentFor(pathname));
}

/**
 * Friendly labels for every route in PARENT_MAP. Used by the
 * GlobalBackButton to render "← Daily Essentials" instead of just
 * "BACK" — same pattern premium apps use (Spotify's "← Your Library",
 * Calm's "← Sleep"). Members know exactly where back will take them
 * before tapping. Falls back to "Back" when the resolved parent
 * isn't in this map (top-level routes / unknown).
 */
const ROUTE_LABELS = {
  "/":                       "Home",
  "/daily-essentials":       "Daily Essentials",
  "/biohacking/sleep":       "Sleep Biohacking",
  "/abundance":              "Abundance",
  "/abundance/my-visions":   "My Visions",
  "/my-journeys":            "My Journeys",
  "/private-suite":          "Private Suite",
  "/practices":              "Practices",
  "/networker":              "Excelior",
  "/atelier":                "Atelier",
  "/team-alignment":         "Team Alignment",
  "/admin":                  "Admin",
};

/**
 * Resolve a human label for the parent of the given pathname.
 * Used by the breadcrumb-style back chip.
 */
export function parentLabelFor(pathname) {
  const parent = parentFor(pathname);
  return ROUTE_LABELS[parent] || "Back";
}
